import React, { useRef, useEffect, useMemo } from 'react';
import { AlertCircle, UserRound, Phone, X, Loader2, Scissors, CalendarClock, AlignLeft, Trash2, MessageCircle, Activity } from 'lucide-react';
import { TIME_SLOTS, timeToMinutes } from '../../utils';
import styles from './AppointmentDrawer.module.css';
import dx from './DrawerExtras.module.css';
import { CLIENT_MESSAGES } from '../../../../utils/appointmentMessages';

const fmtWhen = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '');
const NOTIFY_LABEL = { ...Object.fromEntries(Object.entries(CLIENT_MESSAGES).map(([k, v]) => [k, v.short])), STAFF_NEW: 'Booking details', STAFF_SCHEDULE: 'Day schedule' };
const lengthText = (mins) => (mins >= 60 ? `${Math.floor(mins / 60)} hr${mins % 60 ? ` ${mins % 60} min` : ''}` : `${mins} min`);

export default function AppointmentDrawer({
    isOpen, onClose, formData, setFormData, onSubmit, isSubmitting, errorMsg,
    staffList, serviceList, onServiceChange, onStartChange, canDelete, canEdit = true,
    canReschedule = true, canChangeStatus = true, canWhatsApp = true, canStaffMsg = true,
    clientQuery, onClientSearch, clientResults, showClientDropdown, setShowClientDropdown, selectClient,
    appointment, onQuickStatus, onSendWhatsApp, statusMeta = {},
}) {
    const searchRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) setShowClientDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [setShowClientDropdown]);

    // Close with Escape
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const groupedServices = useMemo(() => serviceList.reduce((acc, srv) => {
        const cat = srv.category || 'General Services';
        (acc[cat] = acc[cat] || []).push(srv);
        return acc;
    }, {}), [serviceList]);

    if (!isOpen) return null;

    const isEdit = Boolean(formData.id);
    const length = Math.max(0, timeToMinutes(formData.end) - timeToMinutes(formData.start));
    const endOptions = TIME_SLOTS.filter((t) => timeToMinutes(t.value) > timeToMinutes(formData.start));
    const notifications = (appointment?.notifications || []).slice(-4).reverse();
    const isActive = !['Cancelled', 'No_Show'].includes(formData.status);
    // Saved booking without edit access: details are read only (status / WhatsApp may still be allowed)
    const locked = isEdit && !canEdit;
    const lockSchedule = locked || (isEdit && !canReschedule);
    const FIELDSET = { border: 0, padding: 0, margin: 0, minWidth: 0 };

    return (
        <div className={styles.drawerOverlay} onClick={onClose} role="presentation">
            <div className={styles.drawer} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit appointment' : 'Book appointment'}>
                <div className={styles.drawerHeader}>
                    <div>
                        <h3>{!isEdit ? 'Book Appointment' : locked ? 'Appointment' : 'Edit Appointment'}</h3>
                        <p>{isEdit && appointment?.appointmentNo ? `Booking ${appointment.appointmentNo}` : 'Client, service, staff and time.'}</p>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>

                <div className={styles.drawerBody}>
                    {errorMsg && <div className={styles.errorAlert} role="alert"><AlertCircle size={14} /> {errorMsg}</div>}

                    <form id="aptForm" onSubmit={onSubmit} className={styles.formContent}>
                        <fieldset disabled={locked} style={FIELDSET}>
                        {/* 1. Client */}
                        <div className={styles.sectionBlock}>
                            <h4 className={styles.sectionTitle}><UserRound size={14} className={styles.secIcon} /> 1. Client</h4>
                            <div className={styles.formGroup} ref={searchRef}>
                                <label htmlFor="apt-client">Search or enter name <span className={styles.req}>*</span></label>
                                <div className={styles.inputWrap}>
                                    <UserRound size={14} className={styles.inputIcon} />
                                    <input id="apt-client" type="text" placeholder="Search by name or mobile..." value={clientQuery} onChange={onClientSearch} required autoComplete="off" />
                                </div>
                                {showClientDropdown && clientResults.length > 0 && (
                                    <div className={styles.dropdownMenu}>
                                        {clientResults.map((c) => (
                                            <div key={c._id} className={styles.dropdownItem} onClick={() => selectClient(c)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && selectClient(c)}>
                                                <div className={styles.avatar}>{String(c.name || '?').charAt(0).toUpperCase()}</div>
                                                <div className={styles.clientInfo}><strong>{c.name}</strong><span>{c.phone}</span></div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="apt-phone">Client mobile <span className={styles.req}>*</span></label>
                                <div className={styles.inputWrap}>
                                    <Phone size={14} className={styles.inputIcon} />
                                    <input id="apt-phone" type="tel" inputMode="numeric" placeholder="10-digit number" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} required />
                                </div>
                            </div>
                        </div>

                        {/* 2. Service & staff */}
                        <div className={styles.sectionBlock}>
                            <h4 className={styles.sectionTitle}><Scissors size={14} className={styles.secIcon} /> 2. Service & Staff</h4>
                            <div className={styles.formGroup}>
                                <label htmlFor="apt-service">Service <span className={styles.req}>*</span></label>
                                <select id="apt-service" value={formData.serviceId} onChange={(e) => onServiceChange(e.target.value)} className={styles.standardInput} required>
                                    <option value="" disabled>-- Choose a service --</option>
                                    {Object.keys(groupedServices).map((category) => (
                                        <optgroup key={category} label={category}>
                                            {groupedServices[category].map((srv) => (
                                                <option key={srv._id} value={srv._id}>
                                                    {srv.name}{srv.duration ? ` (${srv.duration} min)` : ''}{srv.effectivePrice || srv.price ? `, ₹${srv.effectivePrice || srv.price}` : ''}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="apt-staff">Staff <span className={styles.req}>*</span></label>
                                <select id="apt-staff" value={formData.staffId} onChange={(e) => setFormData({ ...formData, staffId: e.target.value })} className={styles.standardInput} required>
                                    <option value="" disabled>-- Choose staff --</option>
                                    {staffList.map((s) => <option key={s._id} value={s._id}>{s.name}{s.designation ? ` (${s.designation})` : ''}</option>)}
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="apt-amount">Price (₹)</label>
                                <input id="apt-amount" type="number" min="0" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className={styles.standardInput} />
                            </div>
                        </div>

                        </fieldset>

                        <fieldset disabled={lockSchedule} style={FIELDSET}>
                        {/* 3. Schedule */}
                        <div className={styles.sectionBlock}>
                            <h4 className={styles.sectionTitle}><CalendarClock size={14} className={styles.secIcon} /> 3. Schedule</h4>
                            <div className={styles.formGroup}>
                                <label htmlFor="apt-date">Date <span className={styles.req}>*</span></label>
                                <input id="apt-date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className={styles.standardInput} required />
                            </div>
                            <div className={styles.grid2}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="apt-start">Start <span className={styles.req}>*</span></label>
                                    <select id="apt-start" value={formData.start} onChange={(e) => (onStartChange ? onStartChange(e.target.value) : setFormData({ ...formData, start: e.target.value }))} className={styles.standardInput} required>
                                        {TIME_SLOTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="apt-end">End <span className={styles.req}>*</span></label>
                                    <select id="apt-end" value={formData.end} onChange={(e) => setFormData({ ...formData, end: e.target.value })} className={styles.standardInput} required>
                                        {!endOptions.some((t) => t.value === formData.end) && <option value={formData.end}>{formData.end}</option>}
                                        {endOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className={dx.timeInfo}>
                                Length: <b>{lengthText(length)}</b>. {lockSchedule && isEdit ? 'You cannot change the time of a booking.' : 'Changing the start keeps the same length.'}
                            </div>
                        </div>
                        </fieldset>

                        {/* 4. Status */}
                        <div className={styles.sectionBlock}>
                            <h4 className={styles.sectionTitle}><Activity size={14} className={styles.secIcon} /> 4. Status</h4>
                            <div className={dx.statusRow} role="radiogroup" aria-label="Appointment status">
                                {Object.entries(statusMeta).map(([key, meta]) => (
                                    <button
                                        type="button"
                                        key={key}
                                        role="radio"
                                        aria-checked={formData.status === key}
                                        className={`${dx.statusBtn} ${formData.status === key ? dx.statusActive : ''}`}
                                        style={formData.status === key ? { color: meta.color } : undefined}
                                        onClick={() => onQuickStatus(key)}
                                        disabled={isSubmitting || (isEdit && (key === 'Cancelled' ? !canDelete : !canChangeStatus))}
                                    >
                                        <i style={{ background: meta.color }} /> {meta.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <fieldset disabled={locked} style={FIELDSET}>
                        {/* 5. Notes */}
                        <div className={styles.sectionBlock}>
                            <h4 className={styles.sectionTitle}><AlignLeft size={14} className={styles.secIcon} /> 5. Notes</h4>
                            <div className={styles.formGroup}>
                                <textarea aria-label="Notes" placeholder="Requests or notes for the staff..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="2" className={styles.standardInput} maxLength={1000} />
                            </div>
                        </div>
                        </fieldset>

                        {/* 6. WhatsApp (saved bookings only) */}
                        {isEdit && appointment && (canWhatsApp || canStaffMsg) && (
                            <div className={styles.sectionBlock}>
                                <h4 className={styles.sectionTitle}><MessageCircle size={14} className={styles.secIcon} /> 6. Send on WhatsApp</h4>
                                {canWhatsApp && (
                                    <>
                                        <div className={dx.waLabel}>To client ({appointment.clientPhone})</div>
                                        <div className={dx.waGrid}>
                                            {(isActive ? ['CONFIRMED', 'REMINDER', 'RESCHEDULED', 'THANK_YOU'] : ['CANCELLED']).map((type) => (
                                                <button type="button" key={type} className={dx.waBtn} onClick={() => onSendWhatsApp(type, 'CLIENT')}>
                                                    <MessageCircle size={12} /> {CLIENT_MESSAGES[type].short}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {isActive && canStaffMsg && (
                                    <>
                                        <div className={dx.waLabel}>To staff</div>
                                        <div className={dx.waGrid}>
                                            <button type="button" className={`${dx.waBtn} ${dx.waBtnStaff}`} onClick={() => onSendWhatsApp('STAFF_NEW', 'STAFF')}>
                                                <MessageCircle size={12} /> Booking details
                                            </button>
                                        </div>
                                    </>
                                )}
                                {notifications.length > 0 && (
                                    <ul className={dx.log}>
                                        {notifications.map((n, i) => (
                                            <li key={`${n.at}-${i}`}>
                                                <b>{NOTIFY_LABEL[n.type] || n.type}</b> to {n.audience === 'STAFF' ? 'staff' : 'client'}, {fmtWhen(n.at)}{n.byName ? ` by ${n.byName}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </form>
                </div>

                <div className={styles.drawerFooter}>
                    {isEdit && canDelete && formData.status !== 'Cancelled' && (
                        <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => { if (window.confirm('Cancel this appointment?')) onQuickStatus('Cancelled'); }}
                            disabled={isSubmitting}
                        >
                            <Trash2 size={14} /> Cancel booking
                        </button>
                    )}
                    <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={isSubmitting}>Close</button>
                    {(!isEdit || canEdit) && (
                        <button type="submit" form="aptForm" className={styles.saveBtn} disabled={isSubmitting}>
                            {isSubmitting ? <><Loader2 size={14} className={styles.spin} /> Saving...</> : isEdit ? 'Update Booking' : 'Confirm Booking'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}